"use client";

import * as React from "react";

export type VideoVariant = "mobile" | "desktop";

const MOBILE_QUERY = "(max-width: 1279px)";

// Video time (seconds) at which the card and offer pill animate in, matching the
// design mock-ups: the monitor has settled by then. If the video never starts, they
// are revealed after REVEAL_FALLBACK_MS so the content is never stuck hidden.
const REVEAL_AT: Record<VideoVariant, number> = { mobile: 2.45, desktop: 3.0 };
const REVEAL_FALLBACK_MS = 5000;

// How long a video that has data but is still paused may wait before we treat autoplay
// as refused.
const AUTOPLAY_GRACE_MS = 1000;
// Playback counts as running only once the video has actually advanced this far; a
// `playing` event alone can come right before the browser pauses it again.
const AUTOPLAY_PROGRESS_S = 0.2;

/**
 * Which hero video is playing: 'mobile' or 'desktop' by screen width, 'none' for
 * reduced-motion / data-saver users (the video is paused and they get the still end
 * frame), or null before hydration. The <video> itself is always rendered.
 */
export function useHeroVideoVariant() {
  const [variant, setVariant] = React.useState<VideoVariant | "none" | null>(null);

  React.useEffect(() => {
    const mobile = window.matchMedia(MOBILE_QUERY);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const saveData =
      (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
        ?.saveData === true;

    const update = () => {
      if (reducedMotion.matches || saveData) setVariant("none");
      else setVariant(mobile.matches ? "mobile" : "desktop");
    };
    update();
    mobile.addEventListener("change", update);
    reducedMotion.addEventListener("change", update);
    return () => {
      mobile.removeEventListener("change", update);
      reducedMotion.removeEventListener("change", update);
    };
  }, []);

  return variant;
}

/**
 * Calls onReveal once the video reaches REVEAL_AT, polled per frame so the animation
 * lands on the right moment even when the video starts late. Without a video
 * (variant 'none') it reveals immediately.
 */
export function useRevealAtVideoTime(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  variant: VideoVariant | "none" | null,
  onReveal: () => void,
) {
  React.useEffect(() => {
    if (!variant) return;
    if (variant === "none") {
      onReveal();
      return;
    }

    const video = videoRef.current;
    const fallback = window.setTimeout(onReveal, REVEAL_FALLBACK_MS);
    const isDue = () =>
      !video || video.ended || !!video.error || video.currentTime >= REVEAL_AT[variant];
    // timeupdate keeps working where requestAnimationFrame is paused (background tabs).
    const onTimeUpdate = () => {
      if (isDue()) onReveal();
    };
    let frame = 0;
    const check = () => {
      if (isDue()) {
        onReveal();
        return;
      }
      frame = window.requestAnimationFrame(check);
    };
    video?.addEventListener("timeupdate", onTimeUpdate);
    check();

    return () => {
      window.clearTimeout(fallback);
      window.cancelAnimationFrame(frame);
      video?.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [videoRef, variant, onReveal]);
}

/**
 * Starts the (muted) video and returns true when the browser refuses to autoplay it,
 * e.g. iOS Low Power Mode. play() then rejects with NotAllowedError; some versions don't
 * reject at all, so a video that has data (canplay) but is still paused shortly after
 * also counts, as does one paused again before the end (started, then stopped by the
 * browser). Waiting for canplay keeps slow networks from being mistaken for a refusal,
 * and a hidden tab is skipped since browsers hold autoplay until it's shown.
 */
export function useAutoplayBlocked(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  variant: VideoVariant | "none" | null,
) {
  const [isBlocked, setIsBlocked] = React.useState(false);

  React.useEffect(() => {
    setIsBlocked(false);
    const video = videoRef.current;
    if (!video || !variant) return;
    // Reduced motion / data saver: the server-rendered video may have autoplayed already.
    if (variant === "none") {
      video.pause();
      return;
    }

    let timer = 0;
    let active = true;
    const onTimeUpdate = () => {
      if (video.paused || video.currentTime < AUTOPLAY_PROGRESS_S) return;
      window.clearTimeout(timer);
      setIsBlocked(false);
    };
    const onCanPlay = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (video.paused && !video.ended && !document.hidden) setIsBlocked(true);
      }, AUTOPLAY_GRACE_MS);
    };
    const onPause = () => {
      if (!video.ended) onCanPlay();
    };
    const onVisibilityChange = () => {
      if (!document.hidden && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) onCanPlay();
    };

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("canplay", onCanPlay);
    video.addEventListener("pause", onPause);
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) onCanPlay();
    // React does not always serialize `muted`, which blocks iOS autoplay.
    video.muted = true;
    video.play().catch((error: unknown) => {
      if (active && error instanceof DOMException && error.name === "NotAllowedError") {
        setIsBlocked(true);
      }
    });

    return () => {
      active = false;
      window.clearTimeout(timer);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("pause", onPause);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [videoRef, variant]);

  return isBlocked;
}
